import { trpc, queryClient } from '@/utils/trpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePassword, signOut } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { INACTIVITY_STORAGE_KEY } from '@/hooks';
import { useEffect } from 'react';
import { savePostLoginRedirect } from '@/feature/authentication/utils/postLoginRedirect';

type UseGetCurrentUserProfileOptions = {
  enabled?: boolean;
};

const getHttpStatus = (error: unknown) =>
  (error as { data?: { httpStatus?: number } } | null)?.data?.httpStatus;

// Hook for updating user email
export const useUpdateEmail = () => {
  return useMutation(trpc.usersRouter.updateEmailAddress.mutationOptions());
};

// Hook for updating user name
export const useUpdateName = () => {
  return useMutation(trpc.userSettingsRouter.updateNames.mutationOptions());
};

// Hook for updating user password using AWS Amplify
export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: async (data: {
      currentPassword: string;
      newPassword: string;
    }) => {
      try {
        await updatePassword({
          oldPassword: data.currentPassword,
          newPassword: data.newPassword,
        });
        return {
          success: true,
          message: 'Password updated successfully',
        };
      } catch (error: unknown) {
        const amplifyError = error as { name?: string; message?: string };
        // consolidate all the error messages into general message.
        if (
          amplifyError.name === 'NotAuthorizedException' ||
          amplifyError.name === 'InvalidPasswordException' ||
          amplifyError.name === 'LimitExceededException'
        ) {
          throw new Error('Password is incorrect.');
        }
        throw new Error(amplifyError.message || 'Failed to update password');
      }
    },
  });
};

// Hook for getting current user profile
export const useGetCurrentUserProfile = (
  options: UseGetCurrentUserProfileOptions = {},
) => {
  const navigate = useNavigate();

  const queryResult = useQuery({
    ...trpc.userSettingsRouter.getCurrentUser.queryOptions(),
    enabled: options.enabled ?? true,
    retry: (failureCount, error) => {
      // Don't retry if it's a 401 error
      const errorStatus = getHttpStatus(error);
      if (errorStatus === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (getHttpStatus(queryResult.error) !== 401) return;

    const handleError = async () => {
      localStorage.removeItem(INACTIVITY_STORAGE_KEY);
      toast.error('Your session has expired. Please log in again.');
      savePostLoginRedirect(
        `${window.location.pathname}${window.location.search}`,
      );
      await signOut();
      queryClient.clear();
      navigate('/login');
    };

    handleError();
  }, [navigate, queryResult.error]);

  return queryResult;
};

// Hook to invalidate user profile queries
export const useInvalidateUserProfile = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: trpc.userSettingsRouter.getCurrentUser.queryKey(),
    });
  };
};
